import {FC, useEffect} from 'react';
import {Outlet, useLocation} from 'react-router-dom';
import {Box, useMediaQuery} from '@mui/material';
import UserHeader from './UserHeader';
import UserSidebar from './UserSidebar';
import {templateStyle} from '../Template.styles';
import UserMobHeader from './UserMobHeader';
import {useNotificationSubscription} from '@/utils/hooks';
import {useSignalR} from '@/signal/SignalRContext';
import {addNotification, setNotifications} from '@/store/notification';
import * as signalR from '@microsoft/signalr';
import {useSelector} from 'react-redux';
import {getUserSelector} from '@/store/user';
import {ApiInstanceProvider} from '@/contexts/ApiInstanceContext';
import {userAPI} from '@/core/api';
import {USER_TYPE} from '@/utils/enums';

interface UserTemplateProps {
  children?: React.ReactNode;
}

const UserTemplate: FC<UserTemplateProps> = ({children}) => {
  const location = useLocation();
  const {connection} = useSignalR();
  useNotificationSubscription(connection, setNotifications, addNotification);

  const user = useSelector(getUserSelector);

  const isLaptop = useMediaQuery('(min-width:1200px)');
  const isMobile = useMediaQuery('(max-width:639.98px)');
  const isTabletOrMobile = useMediaQuery('(max-width:1199.98px)');

  const isRafflePage = location.pathname.includes('/raffle/buy-ticket');
  const isBuyTicketsPage = location.pathname.includes('purchase-tickets');
  const isTablesDetailsPage = location.pathname.includes('/tables/details/');
  const isOtherMobilePages = location.pathname.includes('/account-settings') || location.pathname.includes('/profile');
  const isFundMissionsPage = location.pathname.includes('/fundMissions');

  const showMobileHeader =
    (isRafflePage && isTabletOrMobile) ||
    (isBuyTicketsPage && isTabletOrMobile) ||
    (isOtherMobilePages && isMobile) ||
    isTablesDetailsPage;

  const whitePages = ['/purchase-tickets'].some((path) => location.pathname.includes(path));

  useEffect(() => {
    const startConnection = async () => {
      if (!user) return;

      if (connection && connection.state === signalR.HubConnectionState.Disconnected) {
        try {
          await connection.start();
        } catch (err) {
          // console.error('SignalR connection failed: ', err);
        }
      }
    };

    startConnection();

    return () => {
      if (connection && connection.state === signalR.HubConnectionState.Connected) {
        connection.stop();
      }
    };
  }, [connection, user]);

  return (
    <ApiInstanceProvider value={{apiInstance: userAPI, role: USER_TYPE.USER}}>
      <Box>
        {showMobileHeader ? <UserMobHeader /> : <UserHeader />}

        <Box
          sx={{
            ...templateStyle.wrapper,
            background: whitePages ? 'white' : 'var(--neutral-50)',
          }}
        >
          <Box sx={templateStyle.content}>
            {isLaptop && !isFundMissionsPage && <UserSidebar />}

            <Box sx={{...templateStyle.main, ...(isFundMissionsPage && {maxWidth: '1200px'}) }}>{children || <Outlet />}</Box>
          </Box>
        </Box>
      </Box>
    </ApiInstanceProvider>
  );
};

export default UserTemplate;
